import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Search, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reports/sent-emails`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const VIA = {
  gmail: { label: "Gmail", cls: "bg-emerald-100 text-emerald-700" },
  resend: { label: "Backup (Resend)", cls: "bg-amber-100 text-amber-700" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
};
const ViaBadge = ({ via }) => <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${VIA[via]?.cls || "bg-slate-100"}`}>{VIA[via]?.label || via}</span>;
const list = (a) => (a || []).join(", ");

const EmailDialog = ({ id, onClose }) => {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (!id) return setDoc(null);
    axios.get(`${API}/${id}`, auth()).then((r) => setDoc(r.data)).catch(() => toast.error("Couldn't load email"));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sent-email-dialog">
        <DialogHeader><DialogTitle>{doc?.subject || "Email"}</DialogTitle></DialogHeader>
        {!doc ? <Loader2 className="w-5 h-5 animate-spin text-teal" /> : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-[90px_1fr] gap-y-1">
              <span className="text-slate-500">Sent</span><span>{new Date(doc.created_at).toLocaleString()} · <ViaBadge via={doc.via} /></span>
              <span className="text-slate-500">From</span><span>{doc.from}</span>
              <span className="text-slate-500">To</span><span data-testid="sent-email-to">{list(doc.to)}</span>
              {doc.cc?.length > 0 && <><span className="text-slate-500">Cc</span><span>{list(doc.cc)}</span></>}
              {doc.bcc?.length > 0 && <><span className="text-slate-500">Bcc</span><span>{list(doc.bcc)}</span></>}
              {doc.reply_to?.length > 0 && <><span className="text-slate-500">Reply-to</span><span data-testid="sent-email-reply-to">{list(doc.reply_to)}</span></>}
            </div>
            {doc.gmail_error && <p className="text-amber-700">Gmail problem: {doc.gmail_error}</p>}
            {doc.error && <p className="text-red-600">Error: {doc.error}</p>}
            <iframe title="Email content" sandbox="" srcDoc={doc.html || `<pre>${doc.text || ""}</pre>`} className="w-full h-[60vh] border border-slate-200 rounded" data-testid="sent-email-body" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminSentEmailsReport() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [via, setVia] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    axios.get(API, { ...auth(), params: { q, via, page } }).then((r) => setData(r.data)).catch(() => toast.error("Failed to load sent emails"));
  }, [q, via, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <AdminLayout title="Sent Email Log" breadcrumb="Reports / Sent Email Log">
      <div className="space-y-4" data-testid="admin-sent-emails">
        <Link to="/admin/reports"><Button variant="ghost" size="sm" data-testid="back-to-reports"><ChevronLeft className="w-4 h-4 mr-1" /> Back to Reports</Button></Link>
        <Card className="border-slate-200">
          <CardContent className="p-4 lg:p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="bg-teal/10 p-2 rounded-md"><Mail className="w-5 h-5 text-teal" /></div>
              <div className="flex-1">
                <h2 className="font-heading font-bold text-lg text-navy">Sent Email Log</h2>
                <p className="text-sm text-slate-500">Every email the site sent in the last 90 days. Click a row to see the full email.</p>
              </div>
              <div className="flex gap-4 text-right" data-testid="sent-email-counts">
                {Object.entries(VIA).map(([k, v]) => <div key={k}><p className="text-xs text-slate-500">{v.label}</p><p className="text-xl font-bold text-navy">{data?.counts?.[k] || 0}</p></div>)}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search subject or recipient…" className="pl-9" data-testid="sent-email-search" />
              </div>
              <select value={via} onChange={(e) => { setVia(e.target.value); setPage(1); }} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sent-email-via-filter">
                <option value="">All</option>
                {Object.entries(VIA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div> :
              data.items.length === 0 ? <p className="text-center py-10 text-slate-500" data-testid="sent-email-empty">No emails found.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-2 px-3 font-medium">Sent</th><th className="py-2 px-3 font-medium">Subject</th>
                    <th className="py-2 px-3 font-medium">Recipients</th><th className="py-2 px-3 font-medium">Sent through</th>
                  </tr></thead>
                  <tbody>
                    {data.items.map((e) => (
                      <tr key={e.id} onClick={() => setOpen(e.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`sent-email-row-${e.id}`}>
                        <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="py-2 px-3 text-navy font-medium">{e.subject}</td>
                        <td className="py-2 px-3 text-slate-600">{list(e.to)}{e.cc?.length ? <span className="text-slate-400"> · cc {list(e.cc)}</span> : null}</td>
                        <td className="py-2 px-3"><ViaBadge via={e.via} />{(e.error || e.gmail_error) && <p className="text-xs text-red-600 mt-1 max-w-xs truncate" title={e.error || e.gmail_error}>{e.error || e.gmail_error}</p>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data?.pages > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="sent-email-prev">Previous</Button>
                <span>Page {data.page} of {data.pages}</span>
                <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="sent-email-next">Next</Button>
              </div>
            )}
          </CardContent>
        </Card>
        <EmailDialog id={open} onClose={() => setOpen(null)} />
      </div>
    </AdminLayout>
  );
}
